import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPinned, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const COLORS = { same_day: "#dc2626", rush: "#d97706", standard: "#2d7bd0" };
const pin = (color, approx = false) => L.divIcon({
  className: "",
  html: `<div style="background:${approx ? "#fff" : color};width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:${approx ? `3px dashed ${color}` : "2px solid #fff"};box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -20],
});

function Fit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) map.setView(points[0], 13);
    else map.fitBounds(L.latLngBounds(points), { padding: [30, 30] });
  }, [map, points]);
  return null;
}

export function OverviewMap({ isAdmin }) {
  const [data, setData] = useState({ jobs: [], missing: 0 });
  const [server, setServer] = useState("all");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/dashboard/map").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const servers = [...new Set(data.jobs.map((j) => j.server_name).filter(Boolean))];
  const jobs = data.jobs.filter((j) => server === "all" || j.server_name === server);
  const pts = jobs.map((j) => [j.recipient_lat, j.recipient_lng]);

  const geocode = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/jobs/geocode-missing"); toast.success(`Located ${r.geocoded} address(es)`); load(); }
    catch { toast.error("Geocoding failed"); } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="overview-map-card">
      <div className="px-3 sm:px-5 py-3 border-b border-slate-100 flex items-center gap-2 sm:gap-3 flex-wrap">
        <MapPinned className="h-4 w-4 text-[#2d7bd0] shrink-0" />
        <h3 className="text-sm font-semibold">Active jobs map</h3>
        <span className="text-xs text-slate-500" data-testid="overview-map-count">{jobs.length} located{data.approximate ? ` (${data.approximate} approximate)` : ""}{data.missing ? ` · ${data.missing} not yet located` : ""}</span>
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {servers.length > 0 && (
            <Select value={server} onValueChange={setServer}>
              <SelectTrigger className="h-8 w-36 sm:w-44 text-xs" data-testid="overview-map-server-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All servers</SelectItem>{servers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {isAdmin && (data.missing > 0 || data.approximate > 0) && (
            <Button size="sm" variant="outline" className="h-8" onClick={geocode} disabled={busy} data-testid="geocode-missing-button"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />Locate<span className="hidden sm:inline"> addresses</span></Button>
          )}
        </div>
      </div>
      {pts.length === 0 ? (
        <div className="h-[380px] flex items-center justify-center text-sm text-slate-400" data-testid="overview-map-empty">No located active jobs yet.</div>
      ) : (
        <MapContainer center={pts[0]} zoom={11} scrollWheelZoom className="h-[380px] w-full z-0" data-testid="overview-map">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Fit points={pts} />
          {jobs.map((j) => (
            <Marker key={j.id} position={[j.recipient_lat, j.recipient_lng]} icon={pin(COLORS[j.priority] || COLORS.standard, ["street", "area"].includes(j.geocode_precision))}>
              <Popup>
                <div className="text-xs space-y-0.5">
                  <Link to={`/jobs/${j.id}`} className="font-semibold text-[#1f5fa8] underline">{j.job_number}</Link>
                  <div className="font-medium">{j.recipient_name}</div>
                  <div className="text-slate-600">{j.recipient_address}</div>
                  {["street", "area"].includes(j.geocode_precision) && <div className="text-amber-700">Approximate — {j.geocode_precision === "street" ? "street" : "town"} level. Edit the job and pick the address from suggestions to pin it exactly.</div>}
                  <div className="text-slate-500">{j.service_type} · {j.priority.replace("_", " ")}</div>
                  {j.server_name && <div className="text-slate-500">Server: {j.server_name}</div>}
                  {j.due_date && <div className="text-slate-500">Due {new Date(j.due_date).toLocaleDateString()}</div>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}
      <div className="px-5 py-2 text-[11px] text-slate-500 flex gap-4 border-t border-slate-100">
        <span><i className="inline-block h-2.5 w-2.5 rounded-full bg-[#dc2626] mr-1" />Same day</span>
        <span><i className="inline-block h-2.5 w-2.5 rounded-full bg-[#d97706] mr-1" />Rush</span>
        <span><i className="inline-block h-2.5 w-2.5 rounded-full bg-[#2d7bd0] mr-1" />Standard</span>
      </div>
    </Card>
  );
}
